// Story 7-FU-I (ADO-1222), AC1/AC4 — the substitute-CANDIDATE availability read.
// Story 7-FU-K (ADO-1528), AC1 — the read CYCLE moved out of this hook.
//
// A card offering substitutes must establish the CANDIDATES' own stock before it can exclude the
// out-of-stock ones, and the server over-fetches (up to 10) so the panel can still fill after
// that exclusion. This hook is the card's view of that read: the store slice the OOS filter
// evaluates, the refcounted tracking claim, and the three signals the Substitute CTA needs
// (established / pending / failed).
//
// It no longer DRIVES the read. The cycle — attempts, the bounded ladder, terminality and the
// cooldown — belongs to `availability-read-cycle.ts`, which holds one driver per question so the
// ceiling counts requests on the wire rather than rounds counted by whoever happened to be
// watching (ADO-1528 AC1/AC2). This hook subscribes; it never touches the retry register.
//
// WHY THE FOUR RETURNED SIGNALS KEEP THEIR SEMANTICS. `established`/`failed` derive from the
// store's LIVE `checkedSkus`, never latched on the cycle's own stamp, which is what makes a
// failed read SELF-HEAL when a later socket push or reconnect catch-up answers the candidates
// (7-FU-I rounds 11 and 14 — two findings). The cycle's state supplies only "has this question
// settled, and did it settle by failing"; whether candidates are still unanswered is read off the
// store on every render.

import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAvailabilityStore, type AvailabilityEntry } from './useAvailabilityStore';
import { observeReadCycle, readCycleKey, type ReadCycleState } from './availability-read-cycle';

export interface CandidateAvailability {
  /** The candidates' entries. `undefined` = no stock row (never "in stock") — which after a
   *  successful read means NOT STOCKED HERE, an answer the filter acts on. */
  readonly entries: Readonly<Record<string, AvailabilityEntry | undefined>>;
  /** Safe to evaluate the OOS filter: the read for THIS question settled and left no
   *  candidate UNANSWERED — or there is no depot, where the feature degrades. "Answered"
   *  includes "the server has no stock row for it here", which is the ordinary case now the
   *  server over-fetches 10 candidates; it excludes only what a failed read never reached. */
  readonly established: boolean;
  /** The read for THIS question hasn't settled yet; the CTA reads as loading. */
  readonly pending: boolean;
  /** The read for THIS question genuinely FAILED and left at least one candidate's stock
   *  UNANSWERED — as distinct from a successful read that simply returned no stock row
   *  for some (or all) of them. A partial list is never presented as authoritative. */
  readonly failed: boolean;
}

/**
 * Load and watch the availability of `candidateSkus` at `depotId` (`null` = no active
 * depot, where availability is disabled and this no-ops).
 */
export function useCandidateAvailability(
  candidateSkus: readonly string[],
  depotId: string | null,
): CandidateAvailability {
  // Callers build the SKU list with `.map`, so it is a fresh array every render: key it by
  // CONTENT and memo a stable array off that key — a real, declarable effect dependency
  // with no ref written during render. `JSON.stringify` (not `join(',')`) so a SKU
  // containing the delimiter can't collide two different sets into one key (`['A,B']` vs
  // `['A','B']`); SKUs are numeric today, but nothing enforces that.
  const candidateKey = JSON.stringify(candidateSkus);
  const skus = useMemo(() => [...candidateSkus], [candidateKey]);

  // Subscribe ONLY to the candidates' entries, not the whole `entries` object — a
  // whole-object subscription re-renders every card whenever ANY grid SKU settles, so N
  // settling responses cost N full-grid re-renders.
  const entries = useAvailabilityStore(
    useShallow((s) => {
      const picked: Record<string, AvailabilityEntry | undefined> = {};
      for (const sku of skus) picked[sku] = s.entries[sku];
      return picked;
    }),
  );

  // Candidates the store holds no settled ANSWER for at this depot. NOT "candidates with
  // no entry": a successful read legitimately returns no stock row for a SKU that isn't
  // stocked here, and now the server over-fetches up to 10 candidates that is the ordinary
  // case rather than the exception. Deriving unestablished-ness from entry presence made
  // every such candidate look permanently unknown, so a card whose ladder had failed stayed
  // on the terminal error even after a later read answered the question — the answer was
  // "no row", and no entry ever appeared to clear it. `checkedSkus` is the store's own
  // record of what it has an answer for, and it excludes SKUs a read could not establish.
  const uncheckedSkus = useAvailabilityStore(
    useShallow((s) => skus.filter((sku) => !s.checkedSkus.has(sku))),
  );

  // The question: this candidate set at this depot. It changes iff either does, so a depot
  // switch re-subscribes to a different cycle and re-engages the gate.
  const checkKey = readCycleKey(candidateKey, depotId);
  // The cycle's state, KEYED by the question it belongs to, so a state left over from the
  // previous question can never be read as this one's (a depot switch must hold the gate closed
  // until the new depot's read lands, rather than briefly reusing the old depot's verdict).
  const [observed, setObserved] = useState<{ key: string; state: ReadCycleState }>({
    key: checkKey,
    state: 'pending',
  });

  // Claim the candidates as tracked while the caller is mounted, and RELEASE them on
  // unmount. Tracking is what lets a live push flip a substitute out of an open panel (OQ-4)
  // and what gets the SKU into the reconnect catch-up — but at up to 10 candidates per
  // out-of-stock card a SESSION-long interest would grow that set (re-read on every
  // reconnect, filtered against on every push) far faster than the listing itself. The claim
  // is refcounted, so overlapping candidates across cards are independent.
  //
  // The claim is also what makes a cached candidate entry TRUSTWORTHY: the store evicts an
  // entry whose last claim is released precisely because nothing observes it after that
  // (see `releaseSkus`), so the cycle's "already established" skip can only ever
  // short-circuit on stock something is still watching.
  useEffect(() => {
    if (skus.length === 0) return;
    const { retainSkus, releaseSkus } = useAvailabilityStore.getState();
    retainSkus(skus);
    return () => releaseSkus(skus);
  }, [skus]);

  // Observe the cycle. Subscribing is ALL this does: the cycle owner decides whether a request
  // is needed, so a card mounting into a question that is already laddering adds an observer
  // and not a request, however offset in time its mount is (ADO-1528 AC2).
  useEffect(() => {
    if (skus.length === 0) return;
    if (depotId === null) return; // no active depot → availability disabled (degrade)
    return observeReadCycle(skus, depotId, (state) => {
      setObserved({ key: checkKey, state });
    });
  }, [checkKey, candidateKey, skus, depotId]);

  const state = observed.key === checkKey ? observed.state : 'pending';
  const settled = state !== 'pending';
  // A read that FAILED with candidates still UNANSWERED must surface as an error, even when
  // some candidates were already cached — from the listing's own batch, or another card
  // offering the same substitute. Gating this on "no entry at all" (the round-3 `some`
  // heuristic) let one cached candidate mark the whole question resolved, so the panel
  // presented a PARTIAL list as authoritative and the terminal error never appeared, against
  // AC2/AC4 (round 11, F4). Granularity is per-SKU where per-SKU is meaningful — the filter
  // evaluates each candidate on its own entry — and question-level only for "could we ask at
  // all", which is what a failed read actually answers. SL1.0 agrees on both halves: it
  // computed addability per candidate (`getOneRBPLinkage`) and died loudly on a failed query
  // rather than degrading to a shorter list.
  //
  // `uncheckedSkus` is the store's live record of what it has no ANSWER for, so a candidate
  // the server has no stock row for does not hold this true — that question IS answered —
  // and a later push or reconnect catch-up that answers the rest clears it without the cycle
  // running again.
  const failed = depotId !== null && state === 'failed' && uncheckedSkus.length > 0;

  return {
    entries,
    // No depot → availability disabled: degrade, and let the consumer offer the server's
    // candidates as-is (accepted — the not-on-account path still needs an alternative).
    // Gating on the SETTLED state is what holds the gate closed across a depot switch until
    // the new depot's read lands, so the filter never briefly evaluates the entries the
    // previous depot left behind. A settled SUCCESS opens it even when every candidate came
    // back absent: that is a real answer (nothing stocked here), and the consumer's filter
    // turns it into "no substitutes" rather than a false error.
    established: depotId === null || (settled && !failed),
    pending: depotId !== null && skus.length > 0 && !settled,
    failed,
  };
}
